import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORY_NAMES } from "../lib/constants";
import { PlayingCard } from "../components/shared";
import { Header } from "../components/Header";
import { Plus, Trash2, Loader2, Pencil, X, Check, Save } from "lucide-react";

const EMPTY_SCENARIO = {
  title: "",
  category: "preflop",
  difficulty: "beginner",
  positions: { hero: "BTN", villain: "BB" },
  stack_sizes: { hero: 100, villain: 100 },
  blinds: "1/2",
  action_history: [""],
  board_cards: [],
  hero_hand: ["", ""],
  options: [
    { text: "", is_correct: true },
    { text: "", is_correct: false },
    { text: "", is_correct: false },
  ],
  explanation: "",
  tags: [],
};

const ScenarioFormModal = ({ scenario, onSave, onClose, saving }) => {
  const isEdit = !!scenario?.scenario_id;
  const [form, setForm] = useState(() => {
    if (isEdit) {
      return {
        ...scenario,
        action_history: scenario.action_history?.length ? scenario.action_history : [""],
        board_cards: scenario.board_cards || [],
        hero_hand: scenario.hero_hand?.length === 2 ? scenario.hero_hand : ["", ""],
        options: scenario.options?.length >= 2 ? scenario.options.map(o => ({ text: o.text, is_correct: o.is_correct })) : EMPTY_SCENARIO.options,
        tags: scenario.tags || [],
      };
    }
    return { ...EMPTY_SCENARIO, options: EMPTY_SCENARIO.options.map(o => ({ ...o })), action_history: [""] };
  });
  const [tagInput, setTagInput] = useState("");
  const [boardInput, setBoardInput] = useState((scenario?.board_cards || []).join(", "));

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.hero_hand[0] || !form.hero_hand[1]) { toast.error("Hero hand requires 2 cards (e.g. Ah, Kd)"); return; }
    const correctCount = form.options.filter(o => o.is_correct).length;
    if (correctCount !== 1) { toast.error("Exactly one option must be correct"); return; }
    if (form.options.some(o => !o.text.trim())) { toast.error("All options need text"); return; }
    if (!form.explanation.trim()) { toast.error("Explanation is required"); return; }

    const payload = {
      title: form.title.trim(),
      category: form.category,
      difficulty: form.difficulty,
      positions: form.positions,
      stack_sizes: { hero: Number(form.stack_sizes.hero), villain: Number(form.stack_sizes.villain) },
      blinds: form.blinds,
      action_history: form.action_history.filter(a => a.trim()),
      board_cards: boardInput.split(",").map(c => c.trim()).filter(Boolean),
      hero_hand: form.hero_hand,
      options: form.options.map(o => ({ text: o.text, is_correct: o.is_correct })),
      explanation: form.explanation.trim(),
      tags: form.tags,
    };
    onSave(payload, isEdit ? scenario.scenario_id : null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4 pt-8 pb-8">
      <div className="bg-[#121212] border border-white/10 rounded-xl w-full max-w-3xl" data-testid="scenario-form-modal">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-lg font-semibold">{isEdit ? "Edit Scenario" : "Create Scenario"}</h2>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-white" data-testid="close-modal-btn"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Title</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] transition-colors" placeholder="e.g. BTN vs BB 3-bet pot" data-testid="scenario-title-input" />
          </div>
          {/* Category + Difficulty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Category</label>
              <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37]" data-testid="scenario-category-select">
                {Object.entries(CATEGORY_NAMES).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Difficulty</label>
              <select value={form.difficulty} onChange={e => set("difficulty", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37]" data-testid="scenario-difficulty-select">
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          {/* Positions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Hero Position</label>
              <input value={form.positions.hero} onChange={e => set("positions", { ...form.positions, hero: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="BTN" data-testid="hero-position-input" />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Villain Position</label>
              <input value={form.positions.villain} onChange={e => set("positions", { ...form.positions, villain: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="BB" data-testid="villain-position-input" />
            </div>
          </div>
          {/* Stacks + Blinds */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Hero Stack (BB)</label>
              <input type="number" value={form.stack_sizes.hero} onChange={e => set("stack_sizes", { ...form.stack_sizes, hero: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="hero-stack-input" />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Villain Stack (BB)</label>
              <input type="number" value={form.stack_sizes.villain} onChange={e => set("stack_sizes", { ...form.stack_sizes, villain: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="villain-stack-input" />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Blinds</label>
              <input value={form.blinds} onChange={e => set("blinds", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="1/2" data-testid="blinds-input" />
            </div>
          </div>
          {/* Hero Hand */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Hero Hand (2 cards, e.g. Ah Kd)</label>
            <div className="flex gap-3">
              <input value={form.hero_hand[0]} onChange={e => set("hero_hand", [e.target.value, form.hero_hand[1]])} className="w-24 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-center font-mono" placeholder="Ah" data-testid="hero-card-1-input" />
              <input value={form.hero_hand[1]} onChange={e => set("hero_hand", [form.hero_hand[0], e.target.value])} className="w-24 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-center font-mono" placeholder="Kd" data-testid="hero-card-2-input" />
              {form.hero_hand[0] && form.hero_hand[1] && <div className="flex gap-1 items-center">{form.hero_hand.map(c => <PlayingCard key={c} card={c} size="sm" />)}</div>}
            </div>
          </div>
          {/* Board Cards */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Board Cards (comma-separated, e.g. As, Kh, 7d)</label>
            <input value={boardInput} onChange={e => setBoardInput(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" placeholder="As, Kh, 7d" data-testid="board-cards-input" />
          </div>
          {/* Action History */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Action History</label>
            {form.action_history.map((action, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={action} onChange={e => { const n = [...form.action_history]; n[i] = e.target.value; set("action_history", n); }} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder={`e.g. UTG folds`} data-testid={`action-history-${i}`} />
                {form.action_history.length > 1 && <button type="button" onClick={() => set("action_history", form.action_history.filter((_, j) => j !== i))} className="p-2 text-neutral-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => set("action_history", [...form.action_history, ""])} className="text-xs text-[#D4AF37] hover:underline" data-testid="add-action-btn">+ Add action</button>
          </div>
          {/* Options */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Answer Options (select one correct)</label>
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => set("options", form.options.map((o, j) => ({ ...o, is_correct: j === i })))} className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${opt.is_correct ? "border-teal-400 bg-teal-500/20" : "border-neutral-600 hover:border-neutral-400"}`} data-testid={`option-correct-${i}`}>
                  {opt.is_correct && <Check className="w-3.5 h-3.5 text-teal-400" />}
                </button>
                <input value={opt.text} onChange={e => { const n = [...form.options]; n[i] = { ...n[i], text: e.target.value }; set("options", n); }} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder={`Option ${i + 1}`} data-testid={`option-text-${i}`} />
                {form.options.length > 2 && <button type="button" onClick={() => set("options", form.options.filter((_, j) => j !== i))} className="p-1 text-neutral-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            {form.options.length < 5 && <button type="button" onClick={() => set("options", [...form.options, { text: "", is_correct: false }])} className="text-xs text-[#D4AF37] hover:underline" data-testid="add-option-btn">+ Add option</button>}
          </div>
          {/* Explanation */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Explanation</label>
            <textarea value={form.explanation} onChange={e => set("explanation", e.target.value)} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm resize-y" placeholder="Explain why the correct answer is optimal..." data-testid="explanation-input" />
          </div>
          {/* Tags */}
          <div>
            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1.5">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map(tag => (
                <span key={tag} className="flex items-center gap-1 bg-white/5 text-xs px-2 py-1 rounded">
                  {tag}
                  <button type="button" onClick={() => set("tags", form.tags.filter(t => t !== tag))} className="text-neutral-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (tagInput.trim() && !form.tags.includes(tagInput.trim())) { set("tags", [...form.tags, tagInput.trim()]); setTagInput(""); } } }} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="Add tag and press Enter" data-testid="tag-input" />
            </div>
          </div>
        </form>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-medium bg-white/5 text-neutral-300 hover:bg-white/10 transition-colors" data-testid="cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="px-5 py-2 rounded-md text-sm font-semibold bg-[#D4AF37] text-black hover:bg-[#B3922A] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="save-scenario-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", difficulty: "" });
  const [showForm, setShowForm] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    try { const params = {}; if(filter.category)params.category=filter.category; if(filter.difficulty)params.difficulty=filter.difficulty; const r = await api.get("/admin/scenarios",{params}); setScenarios(r.data); } catch { toast.error("Failed to load scenarios"); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const handleSeed = async () => { try { const r = await api.post("/seed"); toast.success(r.data.message); fetchScenarios(); } catch { toast.error("Failed to seed"); } };
  const handleDelete = async (id) => { if (!window.confirm("Delete this scenario?")) return; try { await api.delete(`/admin/scenarios/${id}`); toast.success("Deleted"); fetchScenarios(); } catch { toast.error("Failed to delete"); } };

  const handleSave = async (payload, scenarioId) => {
    setSaving(true);
    try {
      if (scenarioId) {
        await api.put(`/admin/scenarios/${scenarioId}`, payload);
        toast.success("Scenario updated");
      } else {
        await api.post("/admin/scenarios", payload);
        toast.success("Scenario created");
      }
      setShowForm(false);
      setEditingScenario(null);
      fetchScenarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save scenario");
    } finally { setSaving(false); }
  };

  const openEdit = (s) => { setEditingScenario(s); setShowForm(true); };
  const openCreate = () => { setEditingScenario(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingScenario(null); };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page"><Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="text-2xl sm:text-3xl font-semibold mb-2">Admin Dashboard</h1><p className="text-neutral-400">Manage training scenarios</p></div>
          <div className="flex items-center gap-3">
            <button onClick={handleSeed} className="bg-white/5 border border-white/10 text-neutral-300 font-medium rounded-md px-4 py-2 hover:bg-white/10 transition-colors flex items-center gap-2 text-sm" data-testid="seed-database-btn"><Plus className="w-4 h-4" />Seed DB</button>
            <button onClick={openCreate} className="bg-[#D4AF37] text-black font-semibold rounded-md px-4 py-2 hover:bg-[#B3922A] transition-colors flex items-center gap-2" data-testid="create-scenario-btn"><Plus className="w-4 h-4" />New Scenario</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mb-6">
          <select value={filter.category} onChange={(e) => setFilter(f => ({...f, category: e.target.value}))} className="bg-[#121212] border border-white/10 text-white rounded-md px-4 py-2 focus:border-[#D4AF37]" data-testid="filter-category"><option value="">All Categories</option>{Object.entries(CATEGORY_NAMES).map(([k,n])=><option key={k} value={k}>{n}</option>)}</select>
          <select value={filter.difficulty} onChange={(e) => setFilter(f => ({...f, difficulty: e.target.value}))} className="bg-[#121212] border border-white/10 text-white rounded-md px-4 py-2 focus:border-[#D4AF37]" data-testid="filter-difficulty"><option value="">All Difficulties</option><option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option></select>
          <div className="ml-auto text-sm text-neutral-400 flex items-center">{scenarios.length} scenario{scenarios.length !== 1 && "s"}</div>
        </div>
        {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div> : scenarios.length > 0 ? (
          <div className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden"><div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-white/5 bg-[#0A0A0A]"><th className="text-left text-sm text-neutral-400 p-4">Title</th><th className="text-left text-sm text-neutral-400 p-4">Category</th><th className="text-left text-sm text-neutral-400 p-4">Difficulty</th><th className="text-left text-sm text-neutral-400 p-4">Hero Hand</th><th className="text-right text-sm text-neutral-400 p-4">Actions</th></tr></thead><tbody>
            {scenarios.map((s) => (
              <tr key={s.scenario_id} className="border-b border-white/5 hover:bg-white/5">
                <td className="p-4"><div className="font-medium">{s.title}</div><div className="text-xs text-neutral-500 truncate max-w-xs">{s.explanation?.slice(0,60)}...</div></td>
                <td className="p-4"><span className="px-2 py-1 rounded bg-white/5 text-xs">{CATEGORY_NAMES[s.category]}</span></td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${s.difficulty==="beginner"?"bg-green-500/10 text-green-400":s.difficulty==="intermediate"?"bg-yellow-500/10 text-yellow-400":"bg-red-500/10 text-red-400"}`}>{s.difficulty}</span></td>
                <td className="p-4"><div className="flex gap-1">{s.hero_hand?.map((c) => <PlayingCard key={c} card={c} size="sm" />)}</div></td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(s)} className="p-2 text-neutral-400 hover:text-[#D4AF37] transition-colors" data-testid={`edit-scenario-${s.scenario_id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(s.scenario_id)} className="p-2 text-neutral-400 hover:text-red-500 transition-colors" data-testid={`delete-scenario-${s.scenario_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody></table></div></div>
        ) : (
          <div className="text-center py-20"><p className="text-neutral-400 mb-4">No scenarios found</p><button onClick={handleSeed} className="text-[#D4AF37] hover:underline">Seed the database</button></div>
        )}
      </main>
      {showForm && <ScenarioFormModal scenario={editingScenario} onSave={handleSave} onClose={closeForm} saving={saving} />}
    </div>
  );
};
